import { motion } from 'framer-motion';
import { Phone, Mail, Clock, Users, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ContactForm } from './ContactForm';

export const Contact = () => {
  const { t } = useTranslation();

  const contactChannels = [
    {
      icon: Users,
      titleKey: 'contactPage.channels.recruitment',
      value: 'recruitment@mufeed.com',
      href: 'mailto:recruitment@mufeed.com',
    },
    {
      icon: Phone,
      titleKey: 'contactPage.channels.unifiedNumber',
      value: '920026366',
      href: 'tel:920026366',
    },
    {
      icon: Mail,
      titleKey: 'contactPage.channels.email',
      value: 'Info@mufeed.com',
      href: 'mailto:Info@mufeed.com',
    },
  ];

  return (
    <section id="contact" className="py-24 lg:py-32 bg-background relative overflow-hidden">

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-tight text-foreground mb-4">
            <img src="/title-icon.png" alt="" className="w-8 h-8 md:w-10 md:h-10" />
            {t('contact.label')}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {contactChannels.map((channel, index) => (
              <motion.a
                key={channel.titleKey}
                href={channel.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ x: 10, transition: { duration: 0.3 } }}
                className="flex items-start gap-4 glass-card p-6 rounded-xl hover-lift cursor-pointer group block"
                data-testid={`link-contact-channel-${index}`}
              >
                <motion.div
                  className="w-12 h-12 rounded-full glass-card flex items-center justify-center flex-shrink-0 group-hover:glow-primary transition-all duration-300"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <channel.icon className="w-5 h-5 text-primary" />
                </motion.div>
                <div>
                  <h3 className="font-bold text-foreground mb-1 group-hover:text-gradient transition-all text-lg">
                    {t(channel.titleKey)}
                  </h3>
                  <p className="text-primary font-semibold">{channel.value}</p>
                  <div className="flex items-center gap-2 mt-2 text-muted-foreground text-sm">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{t('contactPage.hours')} • {t('contactPage.days')}</span>
                  </div>
                </div>
              </motion.a>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="glass-card rounded-xl p-6 gradient-border"
            >
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-muted-foreground text-sm">
                  {t('contactPage.businessHoursNote')}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="glass-card rounded-xl p-6 gradient-border space-y-3"
            >
              <div className="flex items-center gap-2 font-bold text-foreground">
                <Mail className="w-5 h-5 text-primary" />
                {t('investorsPage.irContact.title')}
              </div>
              <p className="text-muted-foreground text-sm">
                {t('investorsPage.irContact.subtitle')}
              </p>
              <div className="flex flex-wrap gap-3 pt-1">
                <a
                  href="mailto:ir@mufeed.com"
                  data-testid="link-home-contact-ir-email"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm text-foreground hover:border-primary/50 hover:text-primary transition-all duration-300"
                >
                  <Mail className="w-4 h-4" />
                  {t('investorsPage.irContact.email')}: ir@mufeed.com
                </a>
                <a
                  href="https://wa.me/966112782184"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="link-home-contact-ir-whatsapp"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm text-foreground hover:border-primary/50 hover:text-primary transition-all duration-300"
                >
                  <Phone className="w-4 h-4" />
                  {t('investorsPage.irContact.whatsapp')}: +966 11 278 2184
                </a>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="p-8 rounded-2xl border border-border bg-card">
              <ContactForm showTitle />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
